import React, { useState, useEffect } from 'react';
import ApprovalBonusListPresentation from '../presentation/ApprovalBonusList.jsx';
import { message } from 'antd';
import { useApprovalBonusService } from '../service/ApprovalBonus.service.jsx';
import { HomeServiceProvider } from 'src/home/service/home.services.jsx';
import { gql } from 'graphql-tag';
import { withApollo } from 'react-apollo';
import store from 'src/state.js';
// GraphQL query for badge assignments
const QUERY_BADGE_ASSIGNMENTS = gql`
  query QueryBadgeAssignments($companyId: ID!, $nextToken: String) {
    queryBadgeAssignments(
      companyId: $companyId
      nextToken: $nextToken
    ) {
      items {
        approvalStatus
        awardedBy
        id
        badgeId
        badge {
            id
            name
            snappyGrantId
            snappyGrant {
                id
                name
                costInCurrency
                externalSnappyId
                snappyCampaignTierId
            }
        }
        rewardBonus
        companyId
        userGroupId
        userId
        createdAt
        updatedAt
        user {
          id
          firstName
          lastName
          emailAddress
          recognitionEvents {
            currentBudget
          }
        }
        declineReason
      }
      nextToken
    }
  }
`;



const ApprovalBonusListContainer = ({ client }) => {
    const [approvalBonus, setapprovalBonus] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [approvalCount, setApprovalCount] = useState()
    const approvalBonusAPIService = useApprovalBonusService();
    const [nextToken, setNextToken] = useState(null);
    
    // Get company ID from store
    const state = store.getState();
    const companyId = state?.user?.currentUser?.company?.id;

    useEffect(() => {
        if (client && companyId) {
            getApprovalBonusList();
            getApprovalRewardCount();
        }
    }, [client, companyId]);

    const getApprovalBonusList = async () => {
        try {
            setIsLoading(true);
            
            // Use GraphQL query instead of REST API
            const { data, error } = await client.query({
                query: QUERY_BADGE_ASSIGNMENTS,
                variables: {
                    companyId: companyId,
                    nextToken: nextToken
                },
                fetchPolicy: 'network-only' // Don't use cache
            });
            
            if (error) {
                throw error;
            }
            
            if (data?.queryBadgeAssignments?.items) {
                // Transform the data to match the expected format
                const transformedData = data.queryBadgeAssignments.items.map(item => ({
                    id: item.id,
                    recipient: item.user ? `${item.user.firstName} ${item.user.lastName}` : item.userId,
                    bonus: item.rewardBonus || 0,
                    reward: item.badge ? item.badge.name : item.badgeId,
                    created: item.createdAt ? new Date(item.createdAt) : new Date(),
                    completed: item.approvalStatus !== 'Pending' && item.updatedAt ? new Date(item.updatedAt) : null,
                    status: item.approvalStatus || 'Pending',
                    // Store additional data that might be useful
                    userData: item.user,
                    badgeData: item.badge,
                    declineReason: item.declineReason
                }));
                
                setapprovalBonus(transformedData);
                
                // Store next token for pagination if needed
                if (data.queryBadgeAssignments.nextToken) {
                    setNextToken(data.queryBadgeAssignments.nextToken);
                }
            }
        } catch (error) {
            console.error('Error fetching badge assignments:', error);
            message.error('Failed to fetch recognition details! Try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    const getApprovalRewardCount = async () => {
        try {
            // Use the same GraphQL query data to calculate counts
            const { data, error } = await client.query({
                query: QUERY_BADGE_ASSIGNMENTS,
                variables: {
                    companyId: companyId
                },
                fetchPolicy: 'network-only'
            });
            
            if (error) {
                throw error;
            }
            
            if (data?.queryBadgeAssignments?.items) {
                const items = data.queryBadgeAssignments.items;
                
                // Calculate approval status counts
                const approvedCount = items.filter(item => item.approvalStatus === 'Approved').length;
                const pendingCount = items.filter(item => item.approvalStatus === 'Pending' || !item.approvalStatus).length;
                const declinedCount = items.filter(item => item.approvalStatus === 'Declined').length;
                
                // Calculate reward points values
                const totalBonus = items.reduce((sum, item) => sum + (item.rewardBonus || 0), 0);
                const approvedBonus = items
                    .filter(item => item.approvalStatus === 'Approved')
                    .reduce((sum, item) => sum + (item.rewardBonus || 0), 0);
                const pendingBonus = items
                    .filter(item => item.approvalStatus === 'Pending' || !item.approvalStatus)
                    .reduce((sum, item) => sum + (item.rewardBonus || 0), 0);
                
                // Format the data to match the expected structure
                const countData = {
                    approvalStatusCounts: [
                        { name: 'Approved', totals: approvedCount, color: '--green' },
                        { name: 'Pending', totals: pendingCount, color: '--orange' },
                        { name: 'Declined', totals: declinedCount, color: '--red' }
                    ],
                    rewardPointsValues: [
                        { name: 'Total Rewards', totals: totalBonus, color: '--blue' },
                        { name: 'Approved', totals: approvedBonus, color: '--green' },
                        { name: 'Pending', totals: pendingBonus, color: '--orange' }
                    ]
                };
                
                setApprovalCount(countData);
            }
        } catch (error) {
            console.error('Error calculating approval counts:', error);
            message.error('Failed to fetch approval counts');
        }
    };

    return (
        <>
            <HomeServiceProvider>
                <ApprovalBonusListPresentation
                    approvalBonus={approvalBonus}
                    isLoading={isLoading}
                    approvalCount={approvalCount}
                    client={client}
                />
            </HomeServiceProvider>
        </>
    );
};

export default withApollo(ApprovalBonusListContainer);
